import type { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import HeaderBar from '../../components/HeaderBar';
import ModalError, { ModalErrorDetail } from '../../components/Modals/Error';
import { useBaseUrl, useMediaclipKeepAliveUrl, usePhotoCuratorUrl } from '../../helpers/envHooks';
import { ScreenInfo, useDataCollection } from '../../jarvis/dataCollection';
import './index.css';
import { useInputParameters } from '../../helpers/useInputParameters';
import { usePhotobookApiWebHttpClient } from '../../helpers/useJarvisApiWebHttpClient';
import { PhotobookApi, photoUploadEndpoint } from '../../helpers/apis/photobookApi';
import Designer from '../../components/Designer';
import { LocalStorageHelper } from '../../helpers/localStorage';
import { getUserToken } from '../../jarvis/auth';
import { createSubscriber } from '../../jarvis/eventService';
import { EventServicePluginError, Subscriber, SubscriptionHandle } from '@jarvis/jweb-core';
import {
  photoUploadCompleteEvent,
  PhotoUploadCompleteEventInfo,
  publisherId,
  photoManagerUpload,
  cancelPhotoManagerUpload,
} from '../../jarvis/photoManager';

const UploadPage = () => {
  const [hasError, setHasError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<ModalErrorDetail>();
  const [userMediaclipToken, setUserMediaclipToken] = useState('');
  const [projectId, setProjectId] = useState('');
  const [uploadingPhotoIds, setUploadingPhotoIds] = useState<string[]>([]);
  const [uploadedPhotoIds, setUploadedPhotoIds] = useState<string[]>([]);
  const [subscriber, setSubscriber] = useState<Subscriber | undefined>();
  const [subscription, setSubscription] = useState<SubscriptionHandle | undefined>();

  const dataCollection = useDataCollection();

  const inputs = useInputParameters();

  const photoCuratorUrl = usePhotoCuratorUrl();

  const mediaclipKeepAliveUrl = useMediaclipKeepAliveUrl();
  const photobookApi = new PhotobookApi(usePhotobookApiWebHttpClient());

  const screenInfo: ScreenInfo = { name: 'PhotoUpload', activity: 'PhotoUpload-v01' };
  const backButtonName = 'BackButton';
  const pluginBaseUrl = 'com.hp.jarvis.photo.manager.plugin';

  const sendClickEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: '' }, screenInfo);
  };

  const photobookApiBaseUrl = useBaseUrl();

  // Attempt to only allow valid photoIds to be added.
  const addToUploadedPhotoIds = (photoId: string) => {
    if (!uploadingPhotoIds.includes(photoId))
      console.warn('UploadPage::addToUploadedPhotoIds: Uploaded image is NOT the one we are requesting', photoId);
    if (uploadedPhotoIds.includes(photoId))
      console.warn('UploadPage::addToUploadedPhotoIds: Image is already uploaded', photoId);

    if (uploadingPhotoIds.includes(photoId) && !uploadedPhotoIds.includes(photoId))
      setUploadedPhotoIds((oldArray) => [...oldArray, photoId]);
  };

  // Analytics log when page is loaded
  useEffect(() => {
    // Last page will be used if we have a projectId.
    // At that point we can use the designer route to bring the
    // user back to the mediaclip designer.
    LocalStorageHelper.setLastPage('designer');
    dataCollection.sendScreenViewEvent(screenInfo);
  }, [dataCollection]);

  // Create Subscriber once.
  useEffect(() => {
    if (subscriber == undefined) {
      createSubscriber().then((value) => {
        setSubscriber(value);
        console.log('Created a new subscriber');
      });
    }
  }, []);

  // Subscribe to photoUploadCompleteEvent events
  useEffect(() => {
    if (subscriber != undefined && subscription == undefined) {
      console.log(`UploadPage: Subscribe to ${publisherId}, ${photoUploadCompleteEvent}`);
      subscriber
        .subscribe({ publisherId: publisherId, eventName: photoUploadCompleteEvent }, (eventInfo) => {
          console.log('UploadPage: Event Triggered', eventInfo);

          const event = eventInfo.eventData as PhotoUploadCompleteEventInfo;
          if (event.error)
            console.error(`UploadPage: Upload complete for ${event.localPhotoIdentifier} but with error`, event.error);
          else console.log(`UploadPage: Upload complete for ${event.localPhotoIdentifier}`);

          addToUploadedPhotoIds(event.localPhotoIdentifier);
        })
        .then((result) => {
          if ((result as EventServicePluginError).errorType !== undefined) {
            console.error('UploadPage: subscriber.createSubscription: %o', result);
            throw new Error(`Unable to create subscription`);
          }
          setSubscription(result as SubscriptionHandle);
          console.log('UploadPage: Finished turning on event listener.');
        })
        .catch((reason) => {
          console.error('UploadPage: subscriber.createSubscription threw an error : %o', reason);
          throw new Error(`Unable to create subscription`);
        });
    }
  }, [subscriber, subscription]);

  // Read the query params and set states
  useEffect(() => {
    if (inputs.photoIds != null && inputs.photoIds.length > 0) {
      console.log('UploadPage: obtained photoIds query params: ', inputs.photoIds);
      setUploadingPhotoIds(inputs.photoIds);
      setUploadedPhotoIds([]);
    }
  }, [inputs]);

  // States are setup for photo upload to begin
  useEffect(() => {
    if (
      subscriber != undefined &&
      uploadingPhotoIds != null &&
      uploadingPhotoIds.length > 0 &&
      uploadedPhotoIds.length == 0
    ) {
      uploadPhotosAndLaunchDesigner();
    }
  }, [uploadingPhotoIds, uploadedPhotoIds, subscriber]);

  const uploadPhotosAndLaunchDesigner = async () => {
    try {
      console.log('UploadPage::uploadPhotosAndLaunchDesigner');

      // upload all photos
      const photoManagerResponse = await getUserToken()
        .then((userToken) => {
          console.log('UploadPage::uploadPhotosAndLaunchDesigner: Retrieved user token');

          return photoManagerUpload(
            `${photobookApiBaseUrl}${photoUploadEndpoint}`,
            uploadingPhotoIds,
            userToken.tokenValue,
          );
        })
        .catch((error) => {
          console.error('UploadPage::uploadPhotosAndLaunchDesigner: Failed to retrieve user token.', error);
          throw error;
        });

      const responses = photoManagerResponse.result;
      const uploadedPhotosPromise = responses.map(function (response) {
        return photobookApi.waitUntilUploadFinished(response.photoId, response.remaining);
      });

      // Check the status of all of the photos
      await Promise.all(uploadedPhotosPromise)
        .then((mediaclipPhotoIds) => {
          console.log(
            `UploadPage::uploadPhotosAndLaunchDesigner: Create the project using the ${mediaclipPhotoIds.length} uploaded photoIds`,
          );
          subscriber?.unsubscribe();
          createProject(mediaclipPhotoIds);
        })
        .catch((error) => {
          return Promise.reject(new Error(`Failed to upload photo. (${error})`));
        });
    } catch (error) {
      setErrorDetail({
        context: 'Unable to create Project with uploaded photos',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
    }
  };

  const createProject = async (mediaclipPhotoIds: string[]) => {
    try {
      const data = {
        appInstanceId: inputs.appInstanceId,
        experimentId: inputs.experimentId,
        productCategory: inputs.productCategory,
        locale: inputs.locale,
        photoUrns: mediaclipPhotoIds,
      };
      const response = await photobookApi.createProject(data);
      if (response.data) {
        return handleProjectResponse(response);
      } else {
        throw new Error('Missing response from create project');
      }
    } catch (error) {
      setErrorDetail({
        context: 'Unable to create project',
        ...(error instanceof Error && { error: error }),
      });
      setHasError(true);
      console.log(error);
    }
    return Promise.reject();
  };

  const onClickBack = () => {
    console.log('Back button pressed, redirecting to', photoCuratorUrl);
    sendClickEvent(backButtonName);
    subscriber?.unsubscribe();
    cancelPhotoManagerUpload();
    window.location.href = photoCuratorUrl;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleProjectResponse = (response: AxiosResponse<any>) => {
    const {
      projectId: returnedProjectId,
      userMediaclipToken: returnedUserMediaclipToken,
      expiresAt,
      productCategory = 'photobook',
    } = response.data;
    console.log('Project:', response.data);
    LocalStorageHelper.setProject(returnedProjectId, expiresAt, productCategory);
    setUserMediaclipToken(returnedUserMediaclipToken);
    setProjectId(returnedProjectId);
  };

  const thumbnailSrc = (photoId: string) => {
    return `${pluginBaseUrl}://${photoId}/thumb`;
  };

  const uploadCompleteCount = () => {
    return uploadingPhotoIds.filter((photoId) => uploadedPhotoIds.includes(photoId)).length;
  };

  if (hasError) {
    return <ModalError state={hasError} detail={errorDetail as ModalErrorDetail} screenInfo={screenInfo} />;
  }

  if (projectId && userMediaclipToken) {
    return (
      <Designer
        projectId={projectId}
        userMediaclipToken={userMediaclipToken}
        keepAliveUrl={mediaclipKeepAliveUrl}
        dataCollection={dataCollection}
      />
    );
  }

  return (
    <>
      <HeaderBar handleBack={onClickBack} title="Photobook" colorBackground />
      <section className="UploadPage">
        <div className="message">Please wait while we process your selected images.</div>
        <div className="building">
          <div className="spinner" />
          <div className="grid">
            {uploadingPhotoIds.map((id) => (
              <img
                className={`${uploadedPhotoIds.indexOf(id) == -1 ? 'loading' : ''}`}
                key={`cluster-image-${id}`}
                src={thumbnailSrc(id)}
              />
            ))}
          </div>
        </div>
        {uploadingPhotoIds.length > 0 && (
          <div className="uploads-completed">
            {uploadCompleteCount()} / {uploadingPhotoIds.length} images complete
          </div>
        )}
      </section>
    </>
  );
};

export default UploadPage;
